// ─── Design Tokens ───────────────────────────────────────────────────────────
// Single source of truth for colors, breakpoints, and layout constants.

// ─── Colors ──────────────────────────────────────────────────────────────────

export const color = {
  // Brand
  // ⚠ Coupled to the logo assets. `brandPurple` is the exact fill
  // value used in `assets/images/logo-round-purple.svg` (fill="#040240"
  // fill-opacity="0.95"). `brandNavy` is the tonal family used in
  // `assets/images/logo.svg` (#0A1F41 / #0B1F42 / #0B2042 — the logo
  // uses intentional multi-tone shading, so brandNavy is the
  // representative mid-tone, not a one-to-one match of every stop).
  // If either brand color moves, the SVG fills must move in lockstep
  // or the wordmark / icon will drift out of sync with the UI.
  brandPurple: 'rgba(4, 2, 64, 0.95)',

  // Text
  textPrimary: '#000',
  textSecondary: '#5C6979',
  textBody: '#1f2937',
  textNav: '#647678',
  textCardTitle: '#4b5663',
  white: '#fff',
  // Text over dark/video overlays (hero). Both pass WCAG AA against
  // the hero's radial purple at the title/subcopy position; the muted
  // tier exists to establish hierarchy below the full-white title
  // without collapsing contrast.
  textOnDark: 'rgba(255, 255, 255, 0.95)',
  textOnDarkMuted: 'rgba(255, 255, 255, 0.78)',

  // Surfaces
  surfaceWhite: 'rgba(255, 255, 255, 0.58)',
  surfaceWhiteStrong: 'rgba(255, 255, 255, 0.70)',
  /** Translucent brand-purple surface for large filled panels
   *  (e.g. the contact / quote form container). Kept distinct from
   *  `brandPurple` (above) — that one is logo-coupled and must stay
   *  at 0.95 alpha for SVG fidelity. This token drops to 0.86 so the
   *  panel reads as a tinted surface against the page background,
   *  matching the translucent aesthetic of `surfaceWhite*`. Do NOT
   *  use under white text where contrast is critical at the edges. */
  surfacePurple: 'rgba(4, 2, 64, 0.90)',
  surfaceGray: 'hsla(238, 20%, 92%, 0.95)',
  surfaceOverlay: 'rgba(255, 255, 255, 0.17)',
  surfaceMuted: 'rgba(211, 211, 211, 0.17)',
  surfaceHoverMuted: 'rgba(189, 188, 188, 0.17)',
  surfaceHoverGray: 'rgba(205, 201, 201, 0.21)',
  // Hero "ghost" CTA surface (secondary button over video background).
  surfaceGhostTint: 'rgba(163, 161, 161, 0.19)',

  // Borders
  borderLight: 'hsl(0, 0%, 89.8%)',
  borderGray: 'hsl(0, 0%, 80%)',
  borderSubtle: 'rgba(174, 173, 173, 0.22)',
  borderFaint: 'rgba(216, 214, 214, 0.16)',

  // Misc
  // (removed legacy `link: '#0645ad'` — was unreferenced; raw <a>
  //  now inherits via currentColor, see Typography.js.)
  navShadow: 'rgba(12, 13, 46, 0.06)',
  textMuted: '#7a8088',
  textCode: '#596675',
  inputBg: 'hsl(228, 20%, 94%)',
  // Form field ink + placeholder/icon tier. Kept distinct from the
  // content-layer text tokens above: forms sit on translucent-white
  // surfaces where slightly cooler ink + a warmer muted gray read
  // better than textPrimary/textMuted.
  inputInk: '#1a1a2e',
  inputPlaceholder: '#9ca3af',
  iconMuted: '#c1c6c9',
  danger: '#dc2626',
  /** Translucent border used on invalid form fields. Kept as an rgba
   *  of `danger` so the tint reads as "same red, softer" rather than
   *  a second red. Consumed by the `fieldErrorBorder` constant in
   *  sharedForm.js. */
  dangerBorder: 'rgba(220, 38, 38, 0.6)',
  brandNavy: 'rgba(0, 18, 89, 0.95)',
  surfaceLightGray: 'hsla(238, 18%, 96%, 0.95)',
};

// ─── Breakpoints ─────────────────────────────────────────────────────────────

/**
 * Breakpoints. The short scale (xs…xxl) is the intentional ramp used for
 * most responsive work. The five named one-offs (mobileTypo, mobile,
 * mobileLg, smPlus, wide) exist to cover historical breakpoints that are
 * specific to individual components — documenting them here means every
 * media query in the codebase can go through `up()`/`down()` helpers
 * instead of raw `max-width: Npx` literals.
 */
export const bp = {
  xs: 0,
  mobileTypo: 480,  // Typography root font-size step-down.
  mobile: 500,      // Layout NavSpacer + Nav toolbar cutover.
  mobileLg: 520,    // Nav logo shrink on small phones.
  sm: 600,
  smPlus: 650,      // Footer column-stack between sm and md.
  md: 768,
  lg: 1000,
  xl: 1200,
  wide: 1500,       // Footer wide-viewport margin tweak.
  xxl: 1536,
};

/** @param {'xs'|'sm'|'md'|'lg'|'xl'|'xxl'} key */
export const up = (key) => `@media (min-width: ${bp[key]}px)`;

/** @param {'xs'|'sm'|'md'|'lg'|'xl'|'xxl'} key */
export const down = (key) => `@media (max-width: ${bp[key] - 0.02}px)`;

// ─── Layout ──────────────────────────────────────────────────────────────────

export const layout = {
  pageMargin: '10%',
  pageMarginNarrow: '3%',
  navHeight: '75px',
  navHeightMobile: '60px',
  /* Breathing room between a page's last content block and the footer.
     Applied once at the layout level (ContentContainer in Layout.js) so
     individual pages don't hand-tune their own bottom margins and drift
     out of sync. */
  pageBottom: '48px',
};

// ─── Typography ─────────────────────────────────────────────────────────────

/**
 * Type scale. Seven discrete sizes covering every real usage on the
 * site — from small metadata (`xs`) to the hero headline (`hero`).
 * Responsive sizes use clamp() so they scale with viewport.
 *
 * Suggested semantics:
 *   xs      — small metadata, field codes, legal copy
 *   sm      — helper text, small secondary labels (language toggle, etc.)
 *   base    — body text, inputs, regular buttons
 *   md      — emphasized body, card titles
 *   lg      — subtitle-level prose (PageSubtitle, SubCopy)
 *   xl      — section-level headings
 *   display — page titles (h1-level)
 *   hero    — the home-page hero headline
 *
 * Per-component one-offs still happen (italic faces need size bumps,
 * specific contexts need tuning) — reach for these first, deviate only
 * with reason.
 */
export const fontSize = {
  xs:      '0.78rem',
  sm:      '0.85rem',
  base:    '0.95rem',
  md:      '1.05rem',
  lg:      'clamp(1.1rem, 1.2vw + 0.4rem, 1.25rem)',
  xl:      'clamp(1.4rem, 1.5vw + 0.6rem, 1.65rem)',
  display: 'clamp(1.8rem, 2.5vw + 0.5rem, 2.4rem)',
  hero:    'clamp(2rem, 6vw + 0.5rem, 4.5rem)',
};

/**
 * Weight scale. Four tiers — any deviation (e.g. italic face needing
 * 400 where surrounding sans is 500) is a per-component choice but
 * should still start from these as defaults.
 */
export const fontWeight = {
  regular:  400,
  /** 450 — half-step between regular and medium. Used on translucent
   *  dark-ink-on-light surfaces (consumables category papers) where
   *  medium(500) reads heavy and regular(400) reads thin. Deliberate
   *  optical tuning, not a drift. */
  book:     450,
  medium:   500,
  semibold: 600,
  bold:     700,
};

/**
 * Line-height scale. Five tiers spanning display titles (tight) to
 * airy body prose (loose). Before this existed, components hand-tuned
 * line-heights and the site drifted across 8 distinct values
 * (1.1 / 1.2 / 1.35 / 1.4 / 1.5 / 1.55 / 1.6 / 1.65) with no
 * semantic meaning.
 *
 *   tight   — display titles, hero-level headlines
 *   snug    — card/product titles, compact content
 *   normal  — default body, inputs
 *   relaxed — subtitle prose (PageSubtitle)
 *   loose   — long-form body (about, manufacturers, equipments)
 */
export const lineHeight = {
  display: 1.1,   // hero + 404 big headlines — optically tight for large type
  tight:   1.2,   // section titles, card titles
  snug:    1.35,  // compact blocks
  normal:  1.5,   // default body
  relaxed: 1.55,  // subtitle prose (PageSubtitle)
  loose:   1.65,  // long-form body (about, manufacturers, equipments)
};

/**
 * Letter-spacing scale. Semantic, not arbitrary — negative values
 * tighten large display type (optical correction for big glyphs),
 * positive values open small uppercase labels (legibility at small
 * sizes).
 *
 *   tightest — display titles, hero
 *   tight    — medium body, product titles
 *   normal   — default, inherited
 *   wide     — small-cap labels, contact field names
 *   widest   — uppercase kickers (404 status label, etc.)
 */
export const letterSpacing = {
  tightest: '-0.02em',
  tight:    '-0.01em',
  normal:   '0',
  wide:     '0.04em',
  widest:   '0.12em',
};

/**
 * Shared font-family fallback chain. Satoshi / Garamond / Manrope
 * face declarations all use the same system-ui tail — DRY'd here so
 * changing the fallback order happens in one place.
 */
export const fallbackStack = `system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

// ─── Hover + tint ───────────────────────────────────────────────────────────

/**
 * Hover scale tiers. The principle: larger surfaces use smaller
 * scales because the absolute pixel travel is what the eye reads.
 * A 1.02 scale on a 1000px row is ~20px of horizontal expansion,
 * which feels like a jump; a 1.02 scale on a 250px card is ~5px,
 * which feels like a lift.
 *
 *   xs (1.01)  Very wide rows / panels (≥70% viewport).
 *              Equipment-page FirstLevel/SecondLevel papers.
 *   sm (1.02)  Standard cards and category pills.
 *   md (1.03)  Pill buttons / medium CTAs.
 *   lg (1.04)  Small icon buttons that need to read as responsive.
 *
 * Anything outside this range is a deliberate call (e.g. the Contact
 * page's social icons at 1.12, an intentional over-scale because the
 * icon itself is small).
 */
export const hoverScale = {
  xs: 1.01,
  sm: 1.02,
  md: 1.03,
  lg: 1.04,
};

/**
 * Brand-purple tints at useful alpha levels. Previously scattered as
 * rgba literals — here so every surface that layers purple over a
 * lighter background reaches for the same values.
 *
 *   hover  — faint bg on hovered list rows / drawer items
 *   active — slightly stronger bg for current-page / selected rows
 *   shadow — low-alpha brand-tinted drop-shadow (e.g. focused input)
 *   strong — heavier brand tint for focus rings on light surfaces
 */
export const brandTint = {
  hover:  'rgba(4, 2, 64, 0.04)',
  active: 'rgba(4, 2, 64, 0.06)',
  shadow: 'rgba(4, 2, 64, 0.12)',
  strong: 'rgba(4, 2, 64, 0.4)',
};

// ─── Focus ──────────────────────────────────────────────────────────────────

/**
 * Focus-visible ring tokens. A real CSS `outline` with `outline-offset`
 * gives genuine transparent space between the element and the ring —
 * the page/appbar background shows through, no painted white halo that
 * reads as a "second border" against light surfaces. Modern browsers
 * render the outline along the element's border-radius, so rounded
 * elements get rounded rings for free.
 *
 * Consumed by:
 *   - `focusRing` (below) — opt-in mixin for styled-components.
 *   - the global `:focus-visible` rule in `styles/Typography.js` — catches
 *     raw <a>/<button>/[role="button"]/MUI elements that don't have
 *     their own ring.
 *
 * Components can override individual declarations (e.g. per-context
 * inner-element ring transfer in Nav's CartLink) by importing these
 * tokens directly.
 */
export const focusOutline = `2px solid ${color.brandPurple}`;
export const focusOutlineOffset = '2px';

/** Consistent focus-visible ring for styled-components. Use inside a template literal. */
export const focusRing = `
  &:focus-visible {
    outline: ${focusOutline};
    outline-offset: ${focusOutlineOffset};
    box-shadow: none;
  }
`;

// ─── Timing ──────────────────────────────────────────────────────────────────

/* Total duration of the first-visit logo intro, in ms. Must stay in
   sync with the `fadeInOut` keyframe duration in LogoAnimation.js
   (currently 1.2s). A small buffer (~100ms) is added here so the
   IntroProvider's overlay-lift timer fires just after the animation
   completes, even if the image load event cost a frame or two. */
export const INTRO_ANIMATION_MS = 1300;

// ─── Motion ─────────────────────────────────────────────────────────────────

export const easing = {
  standard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  decelerate: 'cubic-bezier(0.0, 0, 0.2, 1)',
  accelerate: 'cubic-bezier(0.4, 0, 1, 1)',
  inOut: 'cubic-bezier(0.45, 0.05, 0.55, 0.95)',
  /** Soft spring-like settle — overshoots slightly then eases to rest. */
  expressive: 'cubic-bezier(0.16, 1, 0.3, 1)',
};

export const duration = {
  fast: '150ms',
  normal: '250ms',
  slow: '350ms',
  collapse: '320ms',
};

export const motion = {
  pageEnterDuration: '600ms',
  pageEnterDistance: '10px',
  childEnterDuration: '500ms',
  childEnterDistance: '12px',
  staggerStep: 80,
};

/** Apply inside any styled-component to kill motion for users who prefer it. */
export const reducedMotion = `
  @media (prefers-reduced-motion: reduce) {
    transition-duration: 0.01ms !important;
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
  }
`;

// ─── Shadows ────────────────────────────────────────────────────────────────

export const shadow = {
  sm: '0 1px 2px rgba(0, 0, 0, 0.05)',
  md: '0 2px 8px rgba(0, 0, 0, 0.08)',
  lg: '0 4px 16px rgba(0, 0, 0, 0.1)',
  brand: '0 4px 12px rgba(4, 2, 64, 0.12)',
};
